import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { PollyLanguage } from '@shared/interfaces/polly';
import { languageFlags, languageLabels } from '../constants';
import { LanguageTab } from '../AssistantScriptDialog.styled';

interface LanguagePickerDialogProps {
  open: boolean;
  nonBaseLangs: PollyLanguage[];
  pickedLangs: PollyLanguage[];
  isTranslating: boolean;
  onPickedLangsChange: (langs: PollyLanguage[]) => void;
  onClose: () => void;
  onTranslate: (langs: PollyLanguage[]) => void;
}

const LanguagePickerDialog = ({
  open,
  nonBaseLangs,
  pickedLangs,
  isTranslating,
  onPickedLangsChange,
  onClose,
  onTranslate,
}: LanguagePickerDialogProps) => {
  const { t } = useTranslation('common');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 0 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 16,
          pb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {t('assistant.scriptDialog.chooseLanguages')}
        <FormControlLabel
          control={
            <Checkbox
              checked={pickedLangs.length === nonBaseLangs.length}
              indeterminate={
                pickedLangs.length > 0 &&
                pickedLangs.length < nonBaseLangs.length
              }
              onChange={(e) =>
                onPickedLangsChange(e.target.checked ? [...nonBaseLangs] : [])
              }
              size="small"
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>{t('assistant.scriptDialog.selectAll')}</Typography>}
          sx={{ mr: 0 }}
        />
      </DialogTitle>
      <DialogContent sx={{ padding: '10px 20px !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mt: 1.5,
          }}
        >
          {nonBaseLangs.map((lang) => {
            const selected = pickedLangs.includes(lang);

            return (
              <LanguageTab
                key={lang}
                active={selected}
                complete={false}
                onClick={() =>
                  onPickedLangsChange(
                    pickedLangs.includes(lang)
                      ? pickedLangs.filter((l) => l !== lang)
                      : [...pickedLangs, lang],
                  )
                }
                sx={{ fontSize: 15 }}
              >
                <span>{languageFlags[lang]}</span>
                <span>{languageLabels[lang]}</span>
              </LanguageTab>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 2.5,
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: 2, py: 1, px: 2 }}
        >
          {t('assistant.scriptDialog.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={pickedLangs.length === 0 || isTranslating}
          onClick={() => {
            onClose();
            onTranslate(pickedLangs);
          }}
          sx={{ textTransform: 'none', borderRadius: 2, py: 1, px: 2 }}
        >
          {t('assistant.scriptDialog.translate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LanguagePickerDialog;
